import { useTranslation } from 'react-i18next'
import { alpha, Box, Button, Grid, Stack, Typography, useTheme, Tooltip } from '@mui/material'
import { Chart } from 'react-chartjs-2'
import { max, min } from 'lodash'
import {
  ArrowBackIosNewRounded,
  EditRounded,
  InfoRounded,
  WarningRounded,
  HelpOutlineRounded,
} from '@mui/icons-material'
import { useAppSelector } from '../../store/hooks'
import { PTInteractiveResult, PTKldB } from '../../shared/model'
import FsPtInteractiveButtons from './fs-pt-interactive-buttons'
import { downloadInteractive } from './fs-pt-interactive-pdf'
import PTBox from './fs-pt-box'

const FsPtResults = ({
  results,
  setResults,
  selectedKldb,
  salary,
  setFetching,
}: {
  results: PTInteractiveResult[]
  setResults: (results: PTInteractiveResult[]) => void
  selectedKldb: PTKldB | null
  salary: number
  setFetching: (fetching: boolean) => void
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { currentLanguage, company } = useAppSelector((state) => state.currentUser)

  const onExportPDF = async (expOptions: number[], lang: string) => {
    setFetching(true)
    await downloadInteractive(
      expOptions,
      lang,
      results,
      selectedKldb,
      salary,
      company ?? '',
      theme,
      t,
    )
    setFetching(false)
  }

  const onExportCSV = () => {
    try {
      setFetching(true)
      if (!selectedKldb || !salary || !results) return
      const rows = results
        .map(
          (or) =>
            `${
              or.type === 'cwi' ? t('pt.results.cwi.title') : t('pt.results.tat.title')
            },${or.values.map(
              (orr) => `${t(`pt.results.label.${orr.id}` as any)},${orr.salary},${orr.employees}`,
            )}`,
        )
        .flat()
        .join('\n')

      const blob = new Blob([rows], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', 'results.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      alert('something went wrong')
    } finally {
      setFetching(false)
    }
  }

  return (
    <>
      <Stack mb={2} direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" ml={-1}>
          <Button
            variant="text"
            startIcon={<ArrowBackIosNewRounded />}
            onClick={() => {
              setResults([])
            }}
          >
            {t('back')}
          </Button>
        </Stack>
        <FsPtInteractiveButtons onExportPDF={onExportPDF} onExportCSV={onExportCSV} />
      </Stack>
      <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 188px)', pb: 4 }}>
        <PTBox
          title={
            <Stack direction="row" alignItems="center">
              <Typography mr={2} className="font-semibold">
                {t('pt.results.info.title')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditRounded />}
                onClick={() => {
                  setResults([])
                }}
              >
                {t('edit')}
              </Button>
            </Stack>
          }
          content={
            <>
              <Stack direction="row">
                <Typography mr={1} minWidth={141}>{`${t(
                  'pt.assistance.selected.occ.kldb',
                )}:`}</Typography>
                <Typography className="font-semibold">{selectedKldb?.id}</Typography>
              </Stack>
              <Stack direction="row">
                <Typography mr={1} minWidth={141}>{`${t(
                  'pt.assistance.selected.occ.main',
                )}:`}</Typography>
                <Typography className="font-semibold">
                  {currentLanguage === 'de' ? selectedKldb?.titleDe : selectedKldb?.titleEn}
                </Typography>
              </Stack>
              <Stack direction="row">
                <Typography mr={1}>{`${t('pt.assistance.selected.occ.salary')}:`}</Typography>
                <Typography className="font-semibold">
                  {`${salary.toLocaleString(currentLanguage, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} \u20ac`}
                </Typography>
              </Stack>
            </>
          }
          theme={theme}
        />
        <PTBox
          theme={theme}
          title={<Typography className="font-semibold">{t('pt.results.title')}</Typography>}
          content={
            <>
              <Typography variant="body2" className="my-1">
                {t('pt.results.desc')}
              </Typography>
              {results.length > 0
                ? results
                    .sort((a, b) => a.order - b.order)
                    .map((r) => {
                      const orderedVales = r.values.sort((av, bv) => av.order - bv.order)
                      return (
                        <Box mt={1} key={`section_${r.type}`}>
                          <Box
                            p={1}
                            sx={{
                              backgroundColor: theme.palette.action.hover,
                              borderRadius: 1,
                            }}
                          >
                            <Typography>{t('pt.results.within')}</Typography>
                            <Stack direction="row">
                              <Typography className="font-semibold">{`${
                                r.type === 'cwi'
                                  ? t('pt.results.cwi.title')
                                  : t('pt.results.tat.title')
                              } ${r.id}`}</Typography>
                              <Tooltip
                                title={
                                  r.type === 'cwi' ? (
                                    <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
                                      {t('pt.assistance.cwi.tooltip')}
                                    </Typography>
                                  ) : (
                                    <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
                                      {t('pt.assistance.ew.tooltip')}
                                    </Typography>
                                  )
                                }
                                arrow
                                placement="right"
                              >
                                <HelpOutlineRounded
                                  fontSize="small"
                                  sx={{ ml: 0.5, color: theme.palette.action.disabled }}
                                />
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Grid container my={1} px={2}>
                            {orderedVales.map((rv) => {
                              const isSalaryGreater =
                                parseFloat((rv.salary ?? 0).toFixed(2)) >
                                parseFloat(salary.toFixed(2))
                              return (
                                <Grid key={`key_value_${r.type}_${rv.id}`} size={{ md: 4 }}>
                                  <Stack direction="row" alignItems="center" mb={1}>
                                    <Typography>{t(`pt.results.label.${rv.id}` as any)}</Typography>
                                    <Typography variant="body2" color="primary" ml={0.5}>{`(${
                                      rv.employees
                                    } ${t('pt.results.label.employees')})`}</Typography>
                                  </Stack>
                                  {rv.salary !== null ? (
                                    <Box
                                      sx={{
                                        py: 1,
                                        px: 2,
                                        borderRadius: 1,
                                        backgroundColor: isSalaryGreater
                                          ? alpha(theme.palette.error.light, 0.5)
                                          : theme.palette.action.hover,
                                        width: 'fit-content',
                                        minWidth: 200,
                                      }}
                                    >
                                      <Typography>
                                        {`${rv.salary.toLocaleString(currentLanguage, {
                                          maximumFractionDigits: 2,
                                          minimumFractionDigits: 2,
                                        })} \u20ac`}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {rv.salary !== null &&
                                    parseFloat(rv.salary.toFixed(2)) !==
                                      parseFloat(salary.toFixed(2)) && (
                                      <Stack direction="row" alignItems="flex-start" mt={1}>
                                        {isSalaryGreater ? (
                                          <WarningRounded fontSize="small" color="error" />
                                        ) : (
                                          <InfoRounded fontSize="small" color="primary" />
                                        )}
                                        <Typography variant="body2" ml={0.5} mt={0.15} mr={7}>
                                          {isSalaryGreater
                                            ? t('pt.results.below')
                                            : t('pt.results.above')}
                                        </Typography>
                                      </Stack>
                                    )}
                                </Grid>
                              )
                            })}
                          </Grid>
                          <Box mt={4} mb={8} height={350} px={2}>
                            <Typography className="font-semibold">
                              {t('pt.results.graph.title')}
                            </Typography>
                            <Chart
                              type="scatter"
                              className={`${r.type}icongraph`}
                              id={`summary_bar_chart_${r.type}`}
                              data={{
                                labels: orderedVales.map((orv) =>
                                  t(`pt.results.label.${orv.id}` as any),
                                ),
                                datasets: [
                                  {
                                    data: orderedVales.map((orv) => orv.salary),
                                    backgroundColor: theme.palette.primary.main,
                                    pointRadius: 8,
                                    pointHoverRadius: 8,
                                  },
                                ],
                              }}
                              options={{
                                maintainAspectRatio: false,
                                layout: {
                                  padding: {
                                    top: 50,
                                    right: 200,
                                    left: 25,
                                  },
                                },
                                plugins: {
                                  // @ts-ignore
                                  legendMargin: false,
                                  legend: {
                                    display: false,
                                  },
                                  tooltip: {
                                    enabled: false,
                                    mode: 'dataset',
                                  },
                                  datalabels: {
                                    anchor: 'end',
                                    align: 'end',
                                    color: theme.palette.text.secondary,
                                    formatter: (value) =>
                                      `${Number(value).toLocaleString(currentLanguage, {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                      })} \u20ac`,
                                  },
                                  annotation: {
                                    clip: false,
                                    annotations: {
                                      line1: {
                                        type: 'line',
                                        value: salary,
                                        scaleID: 'y',
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: 2,
                                        label: {
                                          backgroundColor: 'transparent',
                                          color: theme.palette.primary.main,
                                          font: {
                                            size: 14,
                                            weight: 'normal',
                                            lineHeight: 1.2,
                                          },
                                          display: true,
                                          content: [
                                            t('pt.results.line1'),
                                            t('pt.results.line2'),
                                            `${Number(salary).toLocaleString(currentLanguage, {
                                              maximumFractionDigits: 2,
                                              minimumFractionDigits: 2,
                                            })} \u20ac`,
                                          ],
                                          position: 'end',
                                          textAlign: 'left',
                                          xAdjust: 150,
                                          yAdjust: 20,
                                        },
                                      },
                                    },
                                  },
                                },
                                scales: {
                                  x: {
                                    grid: {
                                      display: false,
                                    },
                                    border: {
                                      color: theme.palette.text.disabled,
                                    },
                                    ticks: {
                                      color: theme.palette.text.primary,
                                    },
                                    offset: true,
                                    type: 'category',
                                    labels: orderedVales.map(
                                      (orv) => t(`pt.results.label.${orv.id}` as any) as string,
                                    ),
                                  },
                                  y: {
                                    type: 'linear',
                                    offset: true,
                                    min:
                                      (min(orderedVales.map((o) => o.salary)) ?? 0) > salary
                                        ? salary
                                        : min(orderedVales.map((o) => o.salary)) ?? 0,
                                    max:
                                      (max(orderedVales.map((o) => o.salary)) ?? 0) < salary
                                        ? salary
                                        : max(orderedVales.map((o) => o.salary)) ?? 0,
                                    grid: {
                                      display: true,
                                      color: theme.palette.divider,
                                    },
                                    border: {
                                      color: theme.palette.text.disabled,
                                    },
                                    ticks: {
                                      autoSkip: true,
                                      maxTicksLimit: 6,
                                      color: theme.palette.text.primary,
                                      callback: (value) =>
                                        `${Number(value).toLocaleString(currentLanguage, {
                                          maximumFractionDigits: 2,
                                          minimumFractionDigits: 2,
                                        })} \u20ac`,
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      )
                    })
                : null}
            </>
          }
        />
      </Box>
    </>
  )
}

export default FsPtResults
